/* eslint-disable @typescript-eslint/no-explicit-any */
import { GenericTableColumn } from "@ui-components/Table/GenericTable";
import React from "react";
import { ReactComponent as IconEdit } from "../../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../../shared/assets/Icons/Icon_Delete.svg";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { withFallback } from "kontrolEdge/helpers/formattersShappedData";
import { coerce } from "kontrolEdge/helpers/advancedFilters";
import type { ResourceTagSummary } from "kontrolEdge/components/Shared/NameTagEdit/useResourceTags";
import { mapKeListResourceTags } from "kontrolEdge/components/Shared/mapKeListResourceTags";

export interface HardwareItem {
	hardwareId: string;
	name: string;
}
export interface DataCenterItem {
	datacenterId: string;
	name: string;
}

export interface Port {
	id: number;
	hardwareResourceId: number //This comes from Hardware ep
	portName: string;
	portId: string;
	portNumber: string;
	portType: string;
	portStatus: string;
	isAvailable: boolean | null;
	clientAssignation?: string;
	// New fields from KE ports API
	subscriptionId?: string;
	isDraft?: boolean;
	name?: string;
	hardwareId?: string;
	capacityMbps?: number;
	provider?: string | null;
	lagId?: string | null;
	createdAt?: string;
	updatedAt?: string;
	datacenter?: {
		datacenterId: string;
		name: string;
		displayName: string;
		city?: string;
	};
	tenant?: {
		id: number;
		Name: string;
		nameDisplay: string;
		chargebeeCustomerId?: string;
	};
	odooMetadata?: {
		lineId: number;
		orderId: number;
		correlation: string;
	};
	config?: any;
	/** Rich pivot tags from TaggedResources (§5.5). */
	tagsRich?: ResourceTagSummary[];
}

// ranks availability: true > false > unknown
const availabilityRank = (v?: boolean | null) => {
	if (v === true)  return 3; // Active
	if (v === false) return 1; // Down
	return 0;                  // Unknown
  };

export const columns: GenericTableColumn<Port>[] = [
	{
		header: "Port Name",
		accessor: "portName",
	},
	{
		header: "Port ID",
		accessor: "subscriptionId",
		renderCell: (row) => row.subscriptionId || row.portId || String(row.id),
	},
	{
		header: "Port Type",
		accessor: "portType",
		renderCell: (row) => {
			// Virtual ports have config object, physical ports don't
			const isVirtual = !!row.config;
			return isVirtual ? "Virtual" : "Physical";
		},
	},
	{
		header: "DataCenter",
		accessor: "datacenter",
		renderCell: (row) => row.datacenter?.displayName || row.datacenter?.name || row.portStatus || "-",
	},
	{
		header: "Status",
		renderCell: (row) => {
		// Use isDraft if available, otherwise fall back to isAvailable
		const isDraft = row.isDraft;
		const status = row.isAvailable;
		let variant: "success" | "default" | "error" | "warning";
		let label: string;

		if (isDraft !== undefined) {
			// New KE ports API: use isDraft
			variant = isDraft ? "warning" : "success";
			label = isDraft ? "Draft" : "Active";
		} else if (status === true) {
			variant = "success"; label = "Available";
		} else if (status === false) {
			variant = "error";   label = "Down";
		} else if (status == null) {
			variant = "default"; label = "Unknown";
		} else {
			variant = "warning"; label = "Warning";
		}
		return <GenericTag variant={variant} label={label} size="small" />;
		},
		sortAccessor: (row) => {
			if (row.isDraft !== undefined) {
				return row.isDraft ? 1 : 3;
			}
			return availabilityRank(row.isAvailable);
		},
	},
	{
		header: "Actions",
		// We only need a valid string for 'accessor'; real rendering is in renderCell
		accessor: "portType",
		renderCell: (row) => (
			<div className="flex items-center gap-2">
				{/* View Button */}
				<button
					className="
            px-3 py-1
            border border-semantic-border-secondary
            rounded
            text-semantic-text-secondary
            hover:bg-semantic-state-active
          "
					onClick={() => {
						console.log("View data center:", row);
					}}
				>
					View
				</button>

				{/* Edit Button */}
				<button
					className="
            flex items-center justify-center
            border border-semantic-border-secondary
            rounded
            text-semantic-text-secondary
            hover:bg-semantic-state-active
            w-10 h-10
          "
					onClick={() => {
						console.log("Edit data center:", row);
					}}
				>
					<IconEdit className="w-5 h-5" />
				</button>

				{/* Delete Button */}
				<button
					className="
            p-2
            border border-semantic-border-secondary
            rounded
            bg-red-50
            text-red-600
            hover:bg-red-100
          "
					onClick={() => {
						console.log("Delete data center:", row);
					}}
				>
					<IconDelete className="w-5 h-5" />
				</button>
			</div>
		),
	},
]


export const shapePortData = (rows: any[]): Port[] =>
	rows.map((r) => {
		const tagsRich = mapKeListResourceTags(r);
		return {
			id: Number(r.id), // keep id numeric for updates/deletes
			hardwareResourceId: Number(r.hardwareResourceId) || 0,
			portName: withFallback(r.name || r.portName), // Use 'name' from KE API, fallback to portName
			portId: withFallback(r.portId || r.subid), // Use subid as fallback
			portNumber: withFallback(r.portNumber),
			portType: withFallback(r.portType),
			portStatus: withFallback(r.portStatus),
			isAvailable: coerce(r.isAvailable, "boolean") as boolean | null,
			clientAssignation: r.clientAssignation ? String(r.clientAssignation) : undefined,
			// New fields from KE ports API
			subscriptionId: r.subscriptionId || r.subid || undefined,
			isDraft: r.isDraft !== undefined ? Boolean(r.isDraft) : undefined,
			name: r.name || undefined,
			hardwareId: r.hardwareId || undefined,
			capacityMbps: r.capacityMbps ? Number(r.capacityMbps) : undefined,
			provider: r.provider || null,
			lagId: r.lagId || null,
			createdAt: r.createdAt || undefined,
			updatedAt: r.updatedAt || undefined,
			datacenter: r.datacenter ? {
				datacenterId: r.datacenter.datacenterId || "",
				name: r.datacenter.name || "",
				displayName: r.datacenter.displayName || r.datacenter.name || "",
				city: r.datacenter.city || undefined,
			} : undefined,
			tenant: r.tenant ? {
				id: r.tenant.id || 0,
				Name: r.tenant.Name || "",
				nameDisplay: r.tenant.nameDisplay || r.tenant.Name || "",
				chargebeeCustomerId: r.tenant.chargebeeCustomerId || undefined,
			} : undefined,
			odooMetadata: r.odooMetadata ? {
				lineId: Number(r.odooMetadata.lineId) || 0,
				orderId: Number(r.odooMetadata.orderId) || 0,
				correlation: String(r.odooMetadata.correlation || ""),
			} : undefined,
			// Tags from Tags Manager pivot (§5.5).
			tagsRich,
		};
	});
